<template>
  <div class="modal-body">
    <div class="modal-section">
      <h3 class="modal-heading">Ange ny månadsamortering per lån</h3>
      <div class="info-box" role="note">
        <SvgIcon :path="mdiInformationOutline" :size="20" aria-hidden="true" />
        <p v-if="isCappedByMinAmortization">
          Amorteringskravet för det här bolånet, {{ formatAmount(maxAmortization) }} kr/mån, ligger över
          {{ MAX_AMORTIZATION_PER_YEAR_PERCENT }} % av det ursprungliga lånebeloppet per år, så den totala amorteringen
          kan bara sättas till amorteringskravet. För högre höjningar, kontakta Borgo via meddelandefunktionen.
        </p>
        <p v-else>
          Amorteringen kan höjas till max {{ MAX_AMORTIZATION_PER_YEAR_PERCENT }} % av det ursprungliga lånebeloppet per
          år, för det här bolånet {{ formatAmount(maxAmortization) }} kr/mån totalt. För högre höjningar, kontakta Borgo
          via meddelandefunktionen.
        </p>
      </div>
      <InputTable class="amortization-table">
        <thead>
          <tr>
            <th>Lånenummer</th>
            <th>Lånebelopp</th>
            <th>Ränta</th>
            <th>Nuvarande amortering</th>
            <th>Ny amortering</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="loan in changeableLoans" :key="loan.id">
            <InputTableCell>{{ loan.publicLoanNumber }}</InputTableCell>
            <InputTableCell>{{ formatAmountWithCurrency(loan.principal.current) }}</InputTableCell>
            <InputTableCell>
              {{ t(`INTEREST_TERM.${loan.interestTerm.interestTerm}`) }} ({{
                formatPercentage(loan.pricing.interestRate)
              }})
            </InputTableCell>
            <InputTableCell>{{ formatAmount(loan.loanTerms.amortizationPerPeriod) }} kr/mån</InputTableCell>
            <InputTableCell class="input-cell">
              <AppTextField
                v-model="newAmortizations[loan.id]"
                type="amount"
                suffix="kr/mån"
                text-align-right
                borderless
                :aria-label="`Ny månadsamortering för lån ${loan.publicLoanNumber}`"
              />
            </InputTableCell>
          </tr>
          <tr class="total-row">
            <InputTableCell colspan="3">Summa</InputTableCell>
            <InputTableCell>{{ formatAmount(currentTotalAmortization) }} kr/mån</InputTableCell>
            <InputTableCell>
              <template v-if="newTotalAmortization !== null">{{ formatAmount(newTotalAmortization) }} kr/mån</template>
              <template v-else>-</template>
              <br />
              <span v-if="minAmortization !== null" class="text-subtle">
                Amorteringskrav: {{ formatAmount(minAmortization) }} kr/mån
              </span>
            </InputTableCell>
          </tr>
        </tbody>
      </InputTable>

      <p v-if="isBelowMinAmortization" class="modal-error error-text">
        Total amortering behöver vara minst {{ formatAmount(minAmortization) }} kr/mån.
      </p>
      <p v-if="isAboveMaxAmortization" class="modal-error error-text">
        <template v-if="isCappedByMinAmortization">
          Total amortering kan inte vara mer än amorteringskravet, {{ formatAmount(maxAmortization) }} kr/mån.
        </template>
        <template v-else>
          Total amortering kan vara högst {{ formatAmount(maxAmortization) }} kr/mån ({{
            MAX_AMORTIZATION_PER_YEAR_PERCENT
          }}
          % av det ursprungliga lånebeloppet per år).
        </template>
      </p>
    </div>

    <div class="modal-actions">
      <AppButton @click="emit('close')">Avbryt</AppButton>
      <AppButton color="green" :disabled="!canSubmit" :loading="isSubmitting" @click="submit">Spara</AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatAmount, formatAmountWithCurrency, formatPercentage } from '@staff/shared'
import { mdiInformationOutline } from '@mdi/js'
import { AppButton, AppTextField, InputTable, InputTableCell, SvgIcon, notify } from '@staff/ui'
import { changeLoanAmortization, fetchDebtById } from '@/services/debt'
import { effectiveAmortizationPerPeriod as effectiveAmortization } from '@/utils/amortizationDisplay'
import type { Debt } from '../debt.types'

const props = defineProps<{
  debt: Debt
  onSaved: (debt: Debt) => void
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const { t } = useI18n()

// Amortization can only be changed on active mortgage loans (bridge loans are interest-only).
const changeableLoans = computed(() =>
  props.debt.loans.filter((loan) => loan.type === 'mortgage' && loan.lifeCycle.status === 'active'),
)

// AppTextField with type="amount" works with digit-only strings.
const newAmortizations = reactive<Record<string, string>>(
  Object.fromEntries(changeableLoans.value.map((loan) => [loan.id, String(effectiveAmortization(loan))])),
)

const isSubmitting = ref(false)

const minAmortization = computed(() => props.debt.servicingSettings.minAmortization)

// Mirrors MAX_AMORTIZATION_PER_YEAR_PERCENT in bolane-api, which enforces the cap on the
// change-amortization endpoint: the total monthly amortization may be at most this share of
// the active loans' original amount per year, spread over 12 months, or the debt's minimum
// amortization when that is higher. Shown as information only; the API's answer decides.
const MAX_AMORTIZATION_PER_YEAR_PERCENT = 3

const percentCap = computed(() => {
  const totalOriginal = props.debt.loans
    .filter((loan) => loan.lifeCycle.status === 'active')
    .reduce((sum, loan) => sum + loan.principal.original, 0)
  return Math.floor((totalOriginal * MAX_AMORTIZATION_PER_YEAR_PERCENT) / 100 / 12)
})

const isCappedByMinAmortization = computed(() => (minAmortization.value ?? 0) > percentCap.value)

const maxAmortization = computed(() => Math.max(percentCap.value, minAmortization.value ?? 0))

const parsedAmortization = (loanId: string): number | null => {
  const value = newAmortizations[loanId]
  if (value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null
}

const changedLoans = computed(() =>
  changeableLoans.value.flatMap((loan) => {
    const amortizationPerPeriod = parsedAmortization(loan.id)
    if (amortizationPerPeriod === null || amortizationPerPeriod === effectiveAmortization(loan)) return []
    return [{ id: loan.id, amortizationPerPeriod }]
  }),
)

const hasInvalidInput = computed(() => changeableLoans.value.some((loan) => parsedAmortization(loan.id) === null))

// Sums the same values the rows show, so the column adds up.
const currentTotalAmortization = computed(() =>
  changeableLoans.value.reduce((sum, loan) => sum + (loan.loanTerms.amortizationPerPeriod ?? 0), 0),
)

// Null when any field is cleared/invalid — a partial sum would silently mix old and new values.
const newTotalAmortization = computed(() => {
  let sum = 0
  for (const loan of changeableLoans.value) {
    const amortization = parsedAmortization(loan.id)
    if (amortization === null) return null
    sum += amortization
  }
  return sum
})

// The API enforces the debt's amortization rule; this mirrors the known minimum so the
// user gets feedback before saving. When no minimum is set the API's default rule decides.
const isBelowMinAmortization = computed(
  () =>
    minAmortization.value !== null &&
    newTotalAmortization.value !== null &&
    newTotalAmortization.value < minAmortization.value,
)

// Mirrors the API's cap so an over-the-cap total is stopped here instead of answered with a 400.
// Only shown once something is changed: a debt that already amortizes above the cap is not an
// error until the user tries to save a total above it.
const isAboveMaxAmortization = computed(
  () =>
    changedLoans.value.length > 0 &&
    newTotalAmortization.value !== null &&
    newTotalAmortization.value > maxAmortization.value,
)

const canSubmit = computed(
  () =>
    changedLoans.value.length > 0 &&
    !hasInvalidInput.value &&
    !isBelowMinAmortization.value &&
    !isAboveMaxAmortization.value &&
    !isSubmitting.value,
)

const submit = async () => {
  if (!canSubmit.value) return

  isSubmitting.value = true

  try {
    // Only the loans the user actually changed are sent; the API validates the debt total.
    await changeLoanAmortization(props.debt.id, { loans: changedLoans.value })
  } catch (error) {
    isSubmitting.value = false
    notify({
      type: 'error',
      title: 'Kunde inte ändra amortering',
      description: error instanceof Error ? error.message : 'Ett okänt fel uppstod.',
      dismissable: true,
    })
    return
  }

  notify({
    type: 'success',
    title: 'Månadsamorteringen sparades',
  })

  // The change already succeeded; re-read the debt only to refresh the view.
  // fetchDebtById reports its own failure, so a stale view is the worst case.
  try {
    const refreshedDebt = await fetchDebtById(props.debt.id).catch(() => null)
    if (refreshedDebt) props.onSaved(refreshedDebt)
  } catch (_err) {
    notify({
      type: 'error',
      title: 'Amorteringen ändrades men sidan kunde inte uppdateras',
      description: 'Ladda om sidan för att se den nya amorteringen.',
      dismissable: true,
    })
  }
  emit('close')
}
</script>

<style scoped lang="scss">
// A heading inside a modal, dialled down to body size.
.modal-heading {
  font-size: var(--font-size-sm);
  line-height: var(--font-size-sm-line-height);
  font-weight: var(--font-weight-semibold);
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-20);
}

.modal-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-8);
}

.modal-error {
  font-size: var(--font-size-sm);
  line-height: var(--font-size-sm-line-height);
  text-align: right;
}

.info-box {
  display: flex;
  gap: var(--space-8);
  padding: var(--space-12);
  border-radius: var(--radius-md);
  background-color: var(--color-accent-subtle);
  color: var(--color-accent-on-subtle);
  font-size: var(--font-size-sm);
  line-height: var(--font-size-sm-line-height);
}

.amortization-table {
  th,
  td {
    padding: 6px 12px;
    white-space: nowrap;
  }

  // The borderless text field brings its own padding; the cell border is its frame.
  td.input-cell {
    padding: 0;
  }
}

.total-row {
  font-weight: 600;

  td {
    background-color: var(--color-canvas-muted);
  }
}
</style>
